import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { ChangeEvent, MouseEvent, useCallback, useContext, useMemo } from 'react';
import { default as ReactDatePicker, ReactDatePickerProps } from 'react-datepicker';

import { colors } from 'styles/settings';
import { createStyles } from 'utils/createStyle';
import './styles/date-picker.scss';

export type DatePickerProps = ReactDatePickerProps & {
    maxRangeDistanceInMonths?: number;
};

interface RenderCustomHeaderParams {
    monthDate: Date;
    date: Date;
    customHeaderCount: number;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
    prevYearButtonDisabled: boolean;
    nextYearButtonDisabled: boolean;
    onClearAllDates?: () => void;
    changeYear(year: number): void;
    changeMonth(month: number): void;
    decreaseMonth(): void;
    increaseMonth(): void;
    decreaseYear(): void;
    increaseYear(): void;
}

// Demos = https://reactdatepicker.com/
export const DatePicker = ({ ...props }: DatePickerProps) => {
    const { onChange, selectsRange } = props;

    const clearAllDates = useCallback(() => {
        if (selectsRange) {
            onChange([null, null], undefined);
        } else {
            onChange(null, undefined);
        }
    }, [selectsRange, onChange]);

    const { minDate, maxDate, startDate, endDate } = props;

    return (
        <div>
            <DatePickerHeaderContext.Provider value={{ minDate, maxDate, startDate, endDate }}>
                <ReactDatePicker
                    {...props}
                    renderCustomHeader={(params) => (
                        <DatePickerHeader
                            {...{
                                ...params,
                                onClearAllDates: clearAllDates,
                            }}
                        />
                    )}
                />
            </DatePickerHeaderContext.Provider>
        </div>
    );
};

const styles = createStyles({
    input: [
        'text-center',
        {
            width: '3rem',
        },
    ],
    clearAll: 'text-grey-1 absolute left-0 top-2 transform -translate-x-1/2 translate-y-full',
    monthSelector: [
        '',
        {
            padding: '2px',
        },
    ],
    yearSelector: [
        '',
        {
            padding: '2px',
        },
    ],
    grid: [
        'grid items-center',
        {
            gridTemplateColumns: 'auto min-content min-content max-content auto',
            gap: '0.25rem',
        },
    ],
    container: ['pt-2'],
    navBtn: [
        'font-bold block sm:hidden p-4 disabled:bg-brand',
        {
            '&:disabled': {
                color: colors.grey5,
            },
        },
    ],
    close: '',
});

const FIRST_DATE = new Date(1980, 0, 1);

interface DatePickerHeaderContextInfo {
    minDate: Date | null | undefined;
    maxDate: Date | null | undefined;
    startDate: Date | null | undefined;
    endDate: Date | null | undefined;
}

export const DatePickerHeaderContext = React.createContext<DatePickerHeaderContextInfo>({
    minDate: undefined,
    maxDate: undefined,
    startDate: undefined,
    endDate: undefined,
});

const DatePickerHeader = (params: RenderCustomHeaderParams) => {
    const { customHeaderCount } = params;
    const { minDate, maxDate, startDate } = useContext(DatePickerHeaderContext);

    const isFirstCalendar = useMemo(() => customHeaderCount === 0, [customHeaderCount]);

    const years = useMemo(() => {
        const start = (isFirstCalendar ? minDate : startDate || minDate) || FIRST_DATE;
        const end = maxDate || new Date();
        let current = end.getFullYear();

        const availableYears = [];

        while (current >= start.getFullYear()) {
            availableYears.push(current);
            current--;
        }

        return availableYears;
    }, [minDate, maxDate, startDate, isFirstCalendar]);

    const months = useMemo(() => {
        const monthFormatter = new Intl.DateTimeFormat('us', { month: 'long' });

        return Array.from({ length: 12 }).map((_, month) => ({
            value: month,
            name: monthFormatter.format(new Date(2021, month, 1)),
        }));
    }, []);

    const onMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = parseInt(e.target.value, 10) - (isFirstCalendar ? 0 : 1);
        params.changeMonth(value);
    };

    const onYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
        params.changeYear(parseInt(e.target.value, 10));
    };

    const onPrevMonthClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            params.decreaseMonth();
            e.preventDefault();
        },
        [params]
    );

    const onNextMonthClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            params.increaseMonth();
            e.preventDefault();
        },
        [params]
    );

    const renderMonthAndYearSelector = () => {
        return (
            <>
                <div>
                    <select
                        value={params.monthDate.getMonth()}
                        className={styles.monthSelector}
                        onChange={onMonthChange}
                    >
                        {months.map((month) => (
                            <option key={month.value} value={month.value}>
                                {month.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>/</div>
                <div>
                    <select
                        value={params.monthDate.getFullYear()}
                        className={styles.yearSelector}
                        onChange={onYearChange}
                        data-aaa={params.monthDate.getFullYear()}
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.grid}>
                <div>
                    <button
                        disabled={params.prevMonthButtonDisabled}
                        className={classNames(styles.navBtn, 'float-left')}
                        onClick={onPrevMonthClick}
                    >
                        <FontAwesomeIcon style={{ fontSize: '17px' }} icon={faChevronLeft} />
                    </button>
                </div>
                {renderMonthAndYearSelector()}
                <div>
                    <button
                        disabled={params.nextMonthButtonDisabled}
                        className={classNames(styles.navBtn, 'float-right')}
                        onClick={onNextMonthClick}
                    >
                        <FontAwesomeIcon style={{ fontSize: '17px' }} icon={faChevronRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};
